"use client"

import * as React from "react"
import Link from "next/link"
import { SafeImage } from "@/components/ui/safe-image"
import { accentColorFor } from "./types"
import type { IndexBrand } from "./types"

/** Initials fallback for partner tiles without a logo_path. Mirrors
 *  `deriveInitials` in `brand-logo.tsx` so both surfaces look consistent. */
function deriveInitials(name: string): string {
  const cleaned = (name || "").replace(/\(.*?\)/g, "").trim()
  const words = cleaned.split(/[\s&+\-']/).filter(Boolean)
  if (words.length === 0) return "?"
  if (words.length === 1) return words[0].charAt(0).toUpperCase()
  return (words[0].charAt(0) + (words[1] || "").charAt(0)).toUpperCase()
}

interface FeaturedLogoBarProps {
  /** Curated partner brands ordered by `display_order` (Samsung, LG, GE…). */
  partners: IndexBrand[]
}

/**
 * Editor's Pick · Top Refrigerator Brands.
 *
 * Six full brand cards + one orange "+N more" card, laid out as a 7-column
 * grid at md+ so each cell splits the container width evenly. Each card is
 * a structured tile: small rounded logo box + brand name + "{N} models"
 * sub-label below. Matches the editor's-pick band in the handoff design.
 */
export function FeaturedLogoBar({ partners }: FeaturedLogoBarProps) {
  if (partners.length === 0) return null
  const visible = partners.slice(0, 6)

  return (
    <section className="mt-2 mb-8" aria-labelledby="featured-brands-heading">
      <div className="mb-5 flex items-center justify-center gap-2">
        <span className="h-px w-10 bg-[#ff9951]/30" aria-hidden />
        <h2
          id="featured-brands-heading"
          className="text-[11.5px] font-extrabold uppercase tracking-[0.16em] text-[#ff9951]"
        >
          Editor&rsquo;s Pick · Top Refrigerator Brands
        </h2>
        <span className="h-px w-10 bg-[#ff9951]/30" aria-hidden />
      </div>

      {/* Tiles mirror the `MoreBrandsStrip` design on `/brands/[slug]` —
          fixed-height (112px) rounded-[12px] card, generous logo cap
          (max-h-[78px] / max-w-[92%]), 3px hover lift with a strong shadow.
          Keeping the two surfaces visually identical reinforces that this
          band and the related-brands band are the same component family. */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-6">
        {visible.map(b => {
          const hasReal = !!b.logo_path
          const accent = accentColorFor(b.slug)
          return (
            <Link
              key={b.slug}
              href={`/brands/${b.slug}`}
              prefetch={false}
              title={b.name}
              aria-label={b.name}
              className="group grid h-[112px] place-items-center rounded-[12px] border border-gray-200 bg-white p-3 transition-all duration-200 hover:-translate-y-[3px] hover:border-[#ff9951] hover:shadow-[0_14px_28px_-16px_rgba(0,0,0,0.18)]"
            >
              {hasReal ? (
                <SafeImage
                  src={b.logo_path!}
                  alt={`${b.name} logo`}
                  width={300}
                  height={156}
                  sizes="(max-width: 640px) 45vw, (max-width: 1024px) 24vw, 16vw"
                  className="h-auto w-auto max-h-[78px] max-w-[92%] object-contain"
                />
              ) : (
                <div className="grid h-[72px] w-[72px] place-items-center rounded-[10px] bg-gray-50">
                  <span
                    aria-hidden
                    className="text-[28px] font-black leading-none tracking-[-0.02em]"
                    style={{ color: accent }}
                  >
                    {deriveInitials(b.name)}
                  </span>
                </div>
              )}
            </Link>
          )
        })}
      </div>
    </section>
  )
}
